import { parseToDate } from "../../../utils/dateUtils";
import type {
  CleaningAssignment,
  Family,
  SchoolActivity,
  SchoolTask,
  SchoolTaskStatus,
  SchoolTaskType,
} from "../types";

export type ActivityStatus = "draft" | "upcoming" | "ongoing" | "completed";

export interface SchoolTaskFilters {
  search: string;
  classroomId: string;
  type: SchoolTaskType | "";
  status: "available" | "full" | "";
  requirement: "required" | "optional" | "";
  activityId: string;
  locale: string;
}

export interface SchoolActivityRow {
  activity: SchoolActivity;
  linkedTasks: SchoolTask[];
  classroomIds: string[];
  status: ActivityStatus;
}

export interface FamilyObligationRow {
  family: Family;
  status?: SchoolTaskStatus;
  parentRequired: number;
  parentComplete: number;
  generalComplete: boolean;
  generalRequired: number;
  generalCount: number;
  cleaningAssigned: number;
  exempt: boolean;
  complete: boolean;
}

export const getSchoolActivityStatus = (
  activity: SchoolActivity,
  now = new Date(),
): ActivityStatus => {
  if (!activity.startsAt) return "draft";
  const start = parseToDate(activity.startsAt);
  if (!start) return "draft";
  if (start > now) return "upcoming";
  const end = activity.endsAt ? parseToDate(activity.endsAt) : new Date(start);
  if (!end) return "completed";
  if (!activity.endsAt) end.setHours(23, 59, 59, 999);
  return end >= now ? "ongoing" : "completed";
};

export const filterSchoolTasks = (
  tasks: SchoolTask[],
  filters: SchoolTaskFilters,
): SchoolTask[] => {
  const search = filters.search.trim().toLocaleLowerCase(filters.locale);
  return tasks.filter((task) => {
    const isFull = task.signupCount >= task.capacity;
    return (
      (!search || task.name.toLocaleLowerCase(filters.locale).includes(search)) &&
      (!filters.classroomId || task.classroomIds?.includes(filters.classroomId)) &&
      (!filters.type || task.type === filters.type) &&
      (!filters.status || (filters.status === "available" ? !isFull : isFull)) &&
      (!filters.requirement ||
        (filters.requirement === "optional" ? task.optional : !task.optional)) &&
      (!filters.activityId ||
        (filters.activityId === "without"
          ? !task.activity
          : task.activity?.id === filters.activityId))
    );
  });
};

export const getSchoolActivityRows = (
  activities: SchoolActivity[],
  tasks: SchoolTask[],
  now = new Date(),
): SchoolActivityRow[] => activities.map((activity) => {
  const linkedTasks = tasks.filter((task) => task.activity?.id === activity.id);
  const classroomIds = Array.from(
    new Set(linkedTasks.flatMap((task) => task.classroomIds || [])),
  );
  return {
    activity,
    linkedTasks,
    classroomIds,
    status: getSchoolActivityStatus(activity, now),
  };
});

export const filterSchoolActivityRows = (
  rows: SchoolActivityRow[],
  statusFilter: ActivityStatus | "all",
  searchValue: string,
  locale: string,
): SchoolActivityRow[] => {
  const search = searchValue.trim().toLocaleLowerCase(locale);
  return rows.filter(({ activity, status }) =>
    (statusFilter === "all" || status === statusFilter) &&
    (!search || `${activity.name} ${activity.description || ""}`.toLocaleLowerCase(locale).includes(search)),
  );
};

export const getFamilyObligationRows = (
  families: Family[],
  statuses: SchoolTaskStatus[],
  assignments: CleaningAssignment[],
): FamilyObligationRow[] => families.map((family) => {
  const status = statuses.find((item) => item.familyId === family.id);
  const mandatory = status?.mandatoryParentTasks || {};
  const parentRequired = Object.keys(mandatory).length;
  const parentComplete = Object.values(mandatory).filter(Boolean).length;
  const generalComplete = Boolean(status?.generalContributionComplete);
  const generalRequired = status?.generalContributionRequiredCount || 0;
  const generalCount = status?.generalContributionCount || 0;
  const cleaningAssigned = status?.cleaningAssignedCount || 0;
  const exempt = Boolean(status?.exempt);
  const complete = Boolean(status?.overallComplete);
  return {
    family,
    status,
    parentRequired,
    parentComplete,
    generalComplete,
    generalRequired,
    generalCount,
    cleaningAssigned,
    exempt,
    complete,
  };
});
